// Deck pagination model — the single source of truth for HOW report blocks are
// laid out across slides. Both the HTML slide deck (print/PDF) and the PPTX
// export consume this, so the two decks always paginate identically.
//
// Why this exists: the original deck put ONE slide per section and stacked all
// of that section's blocks onto a fixed 16:9 page with overflow hidden — any
// section taller than one slide silently clipped at the bottom (charts losing
// legends, whole blocks disappearing). This model packs blocks across as many
// slides as a section needs, using deterministic height weights (no DOM
// measurement, so it is pure and unit-testable). Oversized content is also
// clamped for slide format: statement-detail row limits are tightened and
// multi-waterfall blocks are split one waterfall per slide.
//
// IMPORTANT: this module has NO imports. It is consumed by BOTH
// QuickBooksCfoPresentationExport.ts (which the Tab imports) and the studio's
// pptx exporter — any runtime import of the Tab here would create a circular
// module graph, so the structural types below are deliberately minimal and the
// few runtime helpers are local (the waterfall defaults must stay in sync with
// the Export's — see DEFAULT_DECK_WATERFALLS).

/** Structural mirror of a report block — both the Tab's and the Export's
 * DeckReportBlock types assign to this. */
export type DeckReportBlock = {
  id: string
  kind: string
  title: string
  section: string
  enabled?: boolean
  source?: Record<string, any>
  layout?: Record<string, any>
  style?: Record<string, any>
  content?: Record<string, any>
}

/** Structural mirror of the presentation config slice the deck needs. */
export type DeckConfig = {
  reportBlocks?: DeckReportBlock[]
  sections?: Record<string, boolean>
  sectionConfig?: Record<string, any>
  waterfalls?: any[]
  commentary?: Record<string, any>
  keyHighlightCount?: number
}

export type DeckContentSlide = {
  kind: 'content'
  sectionKey: string
  heading: string
  /** True when this is the 2nd+ slide of a section (heading gets "continued"). */
  continued: boolean
  blocks: DeckReportBlock[]
}

export type DeckTitleSlide = {
  kind: 'title'
}

export type DeckSlideModel = DeckTitleSlide | DeckContentSlide

export const DECK_SECTION_ORDER: Array<{ key: string; fallbackTitle: string }> = [
  { key: 'home', fallbackTitle: 'Executive Review' },
  { key: 'profitAndLoss', fallbackTitle: 'Profit & Loss' },
  { key: 'balanceSheet', fallbackTitle: 'Balance Sheet' },
  { key: 'cashFlow', fallbackTitle: 'Cash Flow' },
  { key: 'accountsReceivable', fallbackTitle: 'A/R Trends' },
  { key: 'actions', fallbackTitle: 'Priorities & Notes' },
]

/** Slide height budget in abstract units (~16:9 slide body at deck font sizes). */
export const DECK_SLIDE_BUDGET = 10

/** Max statement-detail rows that fit a slide at the deck's 11px table font. */
export const DECK_DETAIL_ROW_LIMIT = 12

const text = (value: unknown): string => String(value ?? '').trim()

const stringArrayLength = (value: unknown): number => (
  Array.isArray(value) ? value.map(text).filter(Boolean).length : 0
)

type DeckWaterfallEntry = { id: string; title?: string; mode?: string; enabled?: boolean }

/** Mirrors the Export's fallback trio when no waterfalls are configured. */
const DEFAULT_DECK_WATERFALLS: DeckWaterfallEntry[] = [
  { id: 'mom-net-income', title: 'Net Income MoM Bridge', mode: 'mom', enabled: true },
  { id: 'yoy-net-income', title: 'Net Income YoY Bridge', mode: 'yoy', enabled: true },
  { id: 'ytd-net-income', title: 'Net Income YTD Bridge', mode: 'ytd', enabled: true },
]

export const deckWaterfallEntries = (config: DeckConfig): DeckWaterfallEntry[] => {
  const raw = Array.isArray((config as any).waterfalls) ? ((config as any).waterfalls as DeckWaterfallEntry[]) : []
  const list = raw.length ? raw : DEFAULT_DECK_WATERFALLS
  return list.filter((entry) => entry && text(entry.id) && entry.enabled !== false)
}

/** Resolve the prose a text block renders (commentary pointer or inline body). */
const deckTextBlockBody = (block: DeckReportBlock, config: DeckConfig): string => {
  const source = (block.source || {}) as Record<string, any>
  const commentary = config.commentary || {}
  if (text(source.commentary) === 'executiveSummary') return text(commentary.executiveSummary)
  const sectionKey = text(source.commentary_section || source.commentarySection)
  if (sectionKey) return text(commentary.sectionSummaries?.[sectionKey])
  return text(block.content?.text)
}

/**
 * Approximate slide-height weight for a block. Deliberately coarse: the goal is
 * "never overflow", not pixel packing, so weights round up. A block whose
 * weight >= the budget simply gets a slide of its own.
 */
export const deckBlockWeight = (block: DeckReportBlock, config: DeckConfig): number => {
  switch (block.kind) {
    case 'key_highlights':
      return DECK_SLIDE_BUDGET
    case 'kpi_grid':
      return 3
    case 'text':
      // ~450 chars per weight unit at the deck's 12px note font.
      return 2 + Math.min(4, Math.ceil(Math.max(1, deckTextBlockBody(block, config).length) / 450))
    case 'statement_rollup':
      return 6
    case 'statement_detail':
      return DECK_SLIDE_BUDGET // always its own slide (clamped to DECK_DETAIL_ROW_LIMIT rows)
    case 'trend_chart':
      return 7
    case 'ratio_card':
      return 7 // stat trio + sparkline + comparison table
    case 'waterfall':
      return 8 // per-waterfall (multi-waterfall blocks are split before weighing)
    case 'ar_summary':
      return DECK_SLIDE_BUDGET // aging bars + commentary + customer table
    case 'action_list': {
      const commentary = config.commentary || {}
      const items = stringArrayLength(commentary.alerts)
        + stringArrayLength(commentary.actions)
        + stringArrayLength(commentary.dataCaveats)
      return Math.min(DECK_SLIDE_BUDGET, 4 + Math.ceil(items / 4))
    }
    default:
      return 4
  }
}

/**
 * Expand blocks into deck-renderable units:
 * - a multi-waterfall block becomes one pseudo-block per enabled waterfall
 *   (`source.waterfall_id` tells the renderer which one to draw), and
 * - statement-detail row limits are clamped to what fits one slide.
 * Block ids stay unique so data-report-block-id attributes remain stable.
 */
export const expandBlocksForDeck = (
  blocks: DeckReportBlock[],
  config: DeckConfig,
): DeckReportBlock[] => {
  const out: DeckReportBlock[] = []
  for (const block of blocks) {
    if (block.kind === 'key_highlights') {
      const count = Math.max(0, Math.min(3, Math.floor(Number(config.keyHighlightCount) || 0)))
      for (let index = 0; index < count; index += 1) {
        out.push({ ...block, id: `${block.id}--${index + 1}`, title: `Key Highlight ${index + 1}`, source: { ...(block.source || {}), highlight_index: index } })
      }
      continue
    }
    if (block.kind === 'waterfall') {
      const waterfalls = deckWaterfallEntries(config)
      if (waterfalls.length <= 1) {
        out.push(block)
        continue
      }
      for (const waterfall of waterfalls) {
        out.push({
          ...block,
          id: `${block.id}--${waterfall.id}`,
          title: text(waterfall.title) || block.title,
          source: { ...(block.source || {}), waterfall_id: waterfall.id },
        })
      }
      continue
    }
    if (block.kind === 'statement_detail') {
      const limit = Math.max(1, Math.min(80, Math.floor(Number(block.layout?.limit) || 32)))
      out.push({
        ...block,
        layout: { ...(block.layout || {}), limit: Math.min(limit, DECK_DETAIL_ROW_LIMIT) },
      })
      continue
    }
    if (block.kind === 'ar_summary') {
      // The full A/R section (commentary + KPI rows + DSO bars + collectability
      // + customer tables) is taller than any slide; split it into an overview
      // slide and a detail slide. `source.ar_part` tells the renderer which
      // half to draw (packet exports leave it unset and render everything).
      out.push({
        ...block,
        id: `${block.id}--overview`,
        source: { ...(block.source || {}), ar_part: 'overview' },
      })
      out.push({
        ...block,
        id: `${block.id}--detail`,
        title: `${block.title} — detail`,
        source: { ...(block.source || {}), ar_part: 'detail' },
      })
      continue
    }
    out.push(block)
  }
  return out
}

/**
 * Paginate the enabled report blocks into slides: title slide first, then each
 * enabled section's blocks packed greedily (in curated order) into as many
 * slides as the section needs. Pure and deterministic. Callers pass a config
 * that has already been through `normalizeConfig`.
 */
export const buildPresentationDeckSlides = (config: DeckConfig): DeckSlideModel[] => {
  const sectionEnabled = (config as any).sections && typeof (config as any).sections === 'object'
    ? (config as any).sections
    : {}
  const sectionConfig = config.sectionConfig || {}
  const enabledBlocks = (Array.isArray(config.reportBlocks) ? config.reportBlocks : [])
    .filter((block) => block && block.enabled !== false)

  const slides: DeckSlideModel[] = [{ kind: 'title' }]
  for (const section of DECK_SECTION_ORDER) {
    if (sectionEnabled[section.key] === false) continue
    const sectionBlocks = expandBlocksForDeck(
      enabledBlocks.filter((block) => block.section === section.key),
      config,
    )
    if (!sectionBlocks.length) continue
    const heading = text((sectionConfig as any)[section.key]?.title) || section.fallbackTitle

    let current: DeckReportBlock[] = []
    let used = 0
    let emitted = 0
    const flush = () => {
      if (!current.length) return
      slides.push({
        kind: 'content',
        sectionKey: section.key,
        heading,
        continued: emitted > 0,
        blocks: current,
      })
      emitted += 1
      current = []
      used = 0
    }
    for (const block of sectionBlocks) {
      const weight = deckBlockWeight(block, config)
      if (current.length && used + weight > DECK_SLIDE_BUDGET) flush()
      current.push(block)
      used += weight
      if (used >= DECK_SLIDE_BUDGET) flush()
    }
    flush()
  }
  return slides
}
